/**
 * @fileoverview The rail's "Favorite settings" group.
 *
 * A whole section of the sidebar we did not have. From the captured tenant rail
 * (fidelity/references/tenant/home), between the main link section and the
 * footer:
 *
 *   li.__sidebar-tabs__14nxjnf
 *     div.__sidebar-group__17uyhwb
 *       div.__sidebar-group-header__17uyhwb .__favorites-header__14nxjnf
 *         div.__sidebar-group-name__17uyhwb          "Favorite settings"
 *         a.bp6-button.bp6-small.bp6-minimal.__view-all-button__14nxjnf  "View all"
 *       ul.__sidebar-group-tabs__17uyhwb
 *         div.__sidebar-no-favorites-wrapper__14nxjnf              (when empty)
 *           a.bp6-button.bp6-fill.bp6-minimal.__sidebar-no-favorites__14nxjnf
 *             star-empty + "View all settings" + arrow-right
 *
 * `li` inside a `ul`, containing a `div` and another `ul` — that nesting is
 * theirs and is reproduced rather than corrected, because the classes are
 * theirs too and the layout depends on the shape.
 *
 * WHAT THIS DOES NOT DO. Upstream also renders a "Suggested favorites based on
 * your recently visited settings" list of five, each with a star toggle. That
 * needs a favourites store and visit history, neither of which exists here yet;
 * inventing five plausible rows would be fabricating a feature. The empty state
 * is the honest branch of the same component, and when favourites land it is
 * the `children` slot below that fills.
 */

import { AnchorButton } from "@blueprintjs/core";
import type { ReactNode } from "react";

import { controlPanelShell as fdy } from "./foundry-parts.generated";

export interface SettingsSidebarFavoritesProps {
  /** Where both the header link and the empty-state CTA go. */
  readonly allSettingsHref: string;
  /** Favourite rows, when there are any. */
  readonly children?: ReactNode;
}

export function SettingsSidebarFavorites({
  allSettingsHref,
  children,
}: SettingsSidebarFavoritesProps): React.ReactElement {
  const hasFavorites = Boolean(children);
  return (
    <li className={fdy["sidebar-tabs"]} data-testid="settings-sidebar-favorites">
      <div className={fdy["sidebar-group"]}>
        <div className={`${fdy["sidebar-group-header"]} ${fdy["favorites-header"]}`}>
          <div className={fdy["sidebar-group-name"]}>Favorite settings</div>
          <AnchorButton
            minimal
            small
            className={fdy["view-all-button"]}
            href={allSettingsHref}
          >
            View all
          </AnchorButton>
        </div>
        <ul className={fdy["sidebar-group-tabs"]}>
          {hasFavorites ? (
            children
          ) : (
            <div className={fdy["sidebar-no-favorites-wrapper"]}>
              <AnchorButton
                fill
                minimal
                className={fdy["sidebar-no-favorites"]}
                href={allSettingsHref}
                icon="star-empty"
                rightIcon="arrow-right"
              >
                <span className={fdy["text-container"]}>View all settings</span>
              </AnchorButton>
            </div>
          )}
        </ul>
      </div>
    </li>
  );
}
